import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  Briefcase, 
  ArrowRightLeft, 
  TrendingUp,
  ChevronRight, 
  ChevronLeft,
  Map,
  Sparkles,
  Rocket,
  Eye
} from 'lucide-react';
import { ParticleBackground } from './animations/ParticleBackground';
import CareerDashboard from './CareerDashboard';

// Hero messages for the carousel
const heroMessages = [
  {
    title: "Accelerate Your Career Growth",
    subtitle: "AI-powered guidance to help you reach your professional goals faster",
    gradient: "from-blue-400 to-cyan-500"
  },
  {
    title: "Master New Skills",
    subtitle: "Personalized learning paths tailored to your career aspirations",
    gradient: "from-purple-400 to-pink-500"
  },
  {
    title: "Navigate Career Changes",
    subtitle: "Expert guidance for successful career transitions",
    gradient: "from-amber-400 to-orange-500"
  }
];

// Career paths with detailed information
const careerPaths = [
  {
    id: 'tech',
    title: "Software Development",
    subtitle: "Build the future with code",
    description: "Master programming, web development, and software engineering",
    icon: <GraduationCap className="h-6 w-6 text-white" />,
    gradient: "from-blue-400 to-cyan-500",
    highlights: ["Full-stack Development", "Cloud Computing", "AI & Machine Learning"],
    type: 'tech'
  },
  {
    id: 'business',
    title: "Business & Management",
    subtitle: "Lead with confidence",
    description: "Develop leadership and management expertise",
    icon: <Briefcase className="h-6 w-6 text-white" />,
    gradient: "from-purple-400 to-pink-500",
    highlights: ["Strategic Planning", "Team Leadership", "Project Management"],
    type: 'business'
  },
  {
    id: 'transition',
    title: "Career Transition",
    subtitle: "Change paths successfully",
    description: "Navigate your career change with confidence",
    icon: <ArrowRightLeft className="h-6 w-6 text-white" />,
    gradient: "from-amber-400 to-orange-500",
    highlights: ["Skills Assessment", "Industry Research", "Transition Planning"],
    type: 'transition'
  },
  {
    id: 'growth',
    title: "Professional Growth",
    subtitle: "Advance in your field",
    description: "Accelerate your career progression",
    icon: <TrendingUp className="h-6 w-6 text-white" />,
    gradient: "from-green-400 to-emerald-500",
    highlights: ["Skill Enhancement", "Career Strategy", "Leadership Development"],
    type: 'growth'
  }
];

export function Hero() {
  const navigate = useNavigate();
  const [currentMessage, setCurrentMessage] = useState(0);
  const [currentPath, setCurrentPath] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartScrollLeft = useRef(0);

  // Auto-advance carousel
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % heroMessages.length);
      setCurrentPath((prev) => (prev + 1) % careerPaths.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartX.current = e.pageX - (carouselRef.current?.offsetLeft || 0);
    dragStartScrollLeft.current = carouselRef.current?.scrollLeft || 0;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    e.preventDefault();
    const x = e.pageX - (carouselRef.current?.offsetLeft || 0);
    const walk = (x - dragStartX.current) * 2;
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = dragStartScrollLeft.current - walk;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    if (!carouselRef.current) return;
    
    const containerWidth = carouselRef.current.offsetWidth;
    const scrollPosition = carouselRef.current.scrollLeft;
    const itemWidth = containerWidth;
    
    const newIndex = Math.round(scrollPosition / itemWidth);
    setCurrentPath(newIndex);
  };

  const handleLearnMoreClick = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div id="hero" className="relative min-h-screen flex items-center">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-slate-900 overflow-hidden">
        <ParticleBackground 
          color="#06b6d4"
          density={40}
          speed={0.3}
          connectParticles={true}
          connectionDistance={160}
          connectionOpacity={0.2}
        />
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.1) 0%, transparent 50%)',
          }}
          animate={{
            opacity: [0.3, 0.5, 0.3],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Animated Header with improved spacing */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentMessage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-center space-y-8 md:space-y-12"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <h1 className="text-4xl md:text-7xl font-bold tracking-tight">
              <motion.span
                className={`bg-gradient-to-r ${heroMessages[currentMessage].gradient} 
                           bg-clip-text text-transparent inline-block`}
                animate={{
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                {heroMessages[currentMessage].title}
              </motion.span>
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 max-w-3xl mx-auto">
              {heroMessages[currentMessage].subtitle}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Preview Dashboard Button with increased spacing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mb-16"
        >
          <button
            onClick={() => setShowDashboard(!showDashboard)}
            className="group relative inline-flex items-center justify-center px-6 py-3 
                     bg-gradient-to-r from-primary-500 to-primary-600
                     text-white font-medium rounded-lg overflow-hidden
                     transform transition-all duration-300"
          >
            <Eye className="mr-2 h-5 w-5" />
            {showDashboard ? 'Hide Preview' : 'Preview Dashboard'}
            <motion.div
              className="absolute inset-0 bg-white/10"
              initial={false}
              animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </button>
        </motion.div>

        {/* Dashboard Preview */}
        <AnimatePresence>
          {showDashboard && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-12 rounded-xl overflow-hidden shadow-2xl border border-white/10"
            >
              <div className="relative">
                {/* Overlay with CTA */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-center p-8 backdrop-blur-sm bg-black/30 rounded-xl">
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Ready to Start Your Journey?
                    </h3>
                    <button
                      onClick={() => navigate('/signup')}
                      className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-lg
                               transform transition-all duration-300 hover:scale-105"
                    >
                      <Rocket className="mr-2 h-5 w-5" />
                      Create Your Account
                    </button>
                  </div>
                </div>
                <CareerDashboard />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Career Path Carousel */}
        <div 
          className="relative max-w-sm md:max-w-2xl mx-auto mt-8 md:mt-12 px-4"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows - Hidden on Mobile */}
          <div className="hidden md:block">
            <motion.button
              onClick={() => setCurrentPath((prev) => (prev - 1 + careerPaths.length) % careerPaths.length)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12
                       text-white/50 hover:text-white transition-colors z-20"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft className="w-8 h-8" />
            </motion.button>
            
            <motion.button
              onClick={() => setCurrentPath((prev) => (prev + 1) % careerPaths.length)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12
                       text-white/50 hover:text-white transition-colors z-20"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight className="w-8 h-8" />
            </motion.button>
          </div>

          {/* Carousel Container */}
          <div className="overflow-hidden relative">
            <motion.div
              ref={carouselRef}
              className="flex"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={handleDragEnd}
              animate={{
                x: `-${currentPath * 100}%`
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
            >
              {careerPaths.map((path, index) => (
                <motion.div
                  key={path.id}
                  className={`w-full flex-shrink-0 p-4 md:p-6 cursor-pointer
                           backdrop-blur-sm bg-white/5 border border-white/10
                           hover:bg-white/10 transition-all duration-300
                           rounded-xl`}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => navigate('/signup', { state: { pathType: path.type }})}
                >
                  {/* Path Content */}
                  <div className="space-y-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br 
                                  ${path.gradient} p-2.5 flex items-center 
                                  justify-center text-white`}>
                      {path.icon}
                    </div>

                    <div>
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                        {path.title}
                      </h3>
                      <p className="text-gray-300">{path.subtitle}</p>
                    </div>

                    <p className="text-gray-400 text-sm md:text-base">{path.description}</p>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-2">
                      {path.highlights.map((highlight, i) => (
                        <span
                          key={i}
                          className={`px-3 py-1 rounded-full text-xs md:text-sm 
                                   bg-gradient-to-r ${path.gradient} bg-opacity-10
                                   text-white/80`}
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {careerPaths.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  setCurrentPath(index);
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 10000);
                }}
                className={`h-2 rounded-full transition-all duration-300 
                         ${index === currentPath 
                           ? `w-8 bg-gradient-to-r ${careerPaths[index].gradient}` 
                           : 'w-2 bg-gray-600'}`}
                whileHover={{ scale: 1.2 }}
                animate={index === currentPath ? {
                  scale: [1, 1.1, 1],
                  transition: {
                    duration: 1,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }
                } : {}}
              />
            ))}
          </div>
        </div>

        {/* CTA Buttons - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 md:mt-12 px-4">
          <motion.button
            onClick={() => navigate('/signup')}
            className="group relative inline-flex items-center justify-center px-6 py-3 
                     bg-gradient-to-r from-primary-500 to-primary-600
                     text-white font-medium rounded-lg overflow-hidden
                     transform transition-all duration-300 w-full sm:w-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10 flex items-center">
              Start Your Journey
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </motion.button>

          <motion.button
            onClick={handleLearnMoreClick}
            className="inline-flex items-center justify-center px-6 py-3 
                     border border-primary-500/30 text-primary-400
                     hover:bg-primary-500/10 rounded-lg
                     group w-full sm:w-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Learn More
            <Map className="ml-2 h-5 w-5 group-hover:translate-y-[-2px] transition-transform" />
          </motion.button>
        </div>
      </div>
    </div>
  );
}